import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { groupApi, userApi } from '../api/client';
import './DashboardPage.css';

interface Repository {
    id: string;
    name: string;
    fullPath: string;
    description?: string;
    isPrivate: boolean;
    updatedAt: string;
    collaboratorRole?: string;
}

interface Group {
    id: string;
    name: string;
    path: string;
    description?: string;
}

export function DashboardPage() {
    const { user } = useAuthStore();
    const [repos, setRepos] = useState<Repository[]>([]);
    const [collaborations, setCollaborations] = useState<Repository[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNewMenu, setShowNewMenu] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [reposData, groupsData, collabsData] = await Promise.all([
                userApi.getRepos(user?.username || '').catch(() => []),
                groupApi.list(1, 20, { parentId: '' }).catch(() => ({ items: [] })),
                userApi.getCollaborations().catch(() => []),
            ]);
            setRepos(Array.isArray(reposData) ? reposData : []);
            setGroups(groupsData.items || []);
            setCollaborations(Array.isArray(collabsData) ? collabsData : []);
        } catch (err) {
            console.error('加载数据失败:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1>欢迎回来，{user?.username}！</h1>
                    <p>这里是您的工作台</p>
                </div>
                <div className="quick-actions">
                    <div className="dropdown">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowNewMenu(!showNewMenu)}
                        >
                            <svg viewBox="0 0 16 16" width="16" height="16">
                                <path
                                    fill="currentColor"
                                    d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm7.25-3.25v2.5h2.5a.75.75 0 0 1 0 1.5h-2.5v2.5a.75.75 0 0 1-1.5 0v-2.5h-2.5a.75.75 0 0 1 0-1.5h2.5v-2.5a.75.75 0 0 1 1.5 0Z"
                                />
                            </svg>
                            新建
                            <svg viewBox="0 0 16 16" width="12" height="12">
                                <path
                                    fill="currentColor"
                                    d="m4.427 7.427 3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 7H4.604a.25.25 0 0 0-.177.427Z"
                                />
                            </svg>
                        </button>
                        {showNewMenu && (
                            <div className="dropdown-menu">
                                <Link to="/new/project" className="dropdown-item">
                                    <svg viewBox="0 0 16 16" width="16" height="16">
                                        <path
                                            fill="currentColor"
                                            d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z"
                                        />
                                    </svg>
                                    新建仓库
                                </Link>
                                <Link to="/groups" className="dropdown-item">
                                    <svg viewBox="0 0 24 24" width="16" height="16">
                                        <path
                                            fill="currentColor"
                                            d="M12 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM10 14c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4Z"
                                        />
                                    </svg>
                                    新建群组
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="dashboard-content">
                {/* 我的项目 */}
                <section className="dashboard-section">
                    <div className="section-header">
                        <h2>我的项目</h2>
                        <Link to={`/${user?.username}`} className="view-all-link">
                            查看全部
                        </Link>
                    </div>
                    {loading ? (
                        <div className="loading-placeholder">加载中...</div>
                    ) : repos.length === 0 ? (
                        <div className="empty-section">
                            <div className="empty-icon">
                                <svg viewBox="0 0 16 16" width="48" height="48">
                                    <path
                                        fill="currentColor"
                                        d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8Z"
                                    />
                                </svg>
                            </div>
                            <h3>还没有项目</h3>
                            <p>创建您的第一个仓库开始编码</p>
                            <Link to="/new/project" className="btn btn-primary">
                                创建仓库
                            </Link>
                        </div>
                    ) : (
                        <div className="project-grid">
                            {repos.slice(0, 6).map((repo) => (
                                <Link key={repo.id} to={`/${repo.fullPath}`} className="project-card" data-repo-path={repo.fullPath}>
                                    <div className="project-icon">
                                        <svg viewBox="0 0 16 16" width="20" height="20">
                                            <path
                                                fill="currentColor"
                                                d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z"
                                            />
                                        </svg>
                                    </div>
                                    <div className="project-info">
                                        <h3>{repo.name}</h3>
                                        <span className="project-path">{repo.fullPath}</span>
                                        {repo.description && (
                                            <p className="project-description">{repo.description}</p>
                                        )}
                                    </div>
                                    {repo.isPrivate && <span className="private-badge">私有</span>}
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                {/* 参与协作的项目 */}
                {collaborations.length > 0 && (
                    <section className="dashboard-section">
                        <div className="section-header">
                            <h2>参与协作的项目</h2>
                        </div>
                        <div className="project-grid">
                            {collaborations.slice(0, 6).map((repo) => (
                                <Link key={repo.id} to={`/${repo.fullPath}`} className="project-card" data-repo-path={repo.fullPath}>
                                    <div className="project-icon">
                                        <svg viewBox="0 0 16 16" width="20" height="20">
                                            <path
                                                fill="currentColor"
                                                d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z"
                                            />
                                        </svg>
                                    </div>
                                    <div className="project-info">
                                        <h3>{repo.name}</h3>
                                        <span className="project-path">{repo.fullPath}</span>
                                        {repo.description && (
                                            <p className="project-description">{repo.description}</p>
                                        )}
                                    </div>
                                    <span className="collab-badge">
                                        {repo.collaboratorRole === 'maintainer' ? '维护者' :
                                            repo.collaboratorRole === 'developer' ? '开发者' : '访客'}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* 我的群组 */}
                <section className="dashboard-section">
                    <div className="section-header">
                        <h2>我的群组</h2>
                        <Link to="/groups" className="view-all-link">
                            查看全部
                        </Link>
                    </div>
                    {loading ? (
                        <div className="loading-placeholder">加载中...</div>
                    ) : groups.length === 0 ? (
                        <div className="empty-section">
                            <div className="empty-icon">
                                <svg viewBox="0 0 24 24" width="48" height="48">
                                    <path
                                        fill="currentColor"
                                        d="M12 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM10 14c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4Z"
                                    />
                                </svg>
                            </div>
                            <h3>还没有群组</h3>
                            <p>创建群组来组织您的项目和团队</p>
                            <Link to="/groups" className="btn btn-secondary">
                                浏览群组
                            </Link>
                        </div>
                    ) : (
                        <div className="group-list">
                            {groups.slice(0, 5).map((group) => (
                                <Link key={group.id} to={`/groups/${group.path}`} className="group-item">
                                    <div className="group-avatar">
                                        {group.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="group-info">
                                        <h3>{group.name}</h3>
                                        <span className="group-path">@{group.path}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                {/* 快速入门 */}
                <section className="dashboard-section">
                    <div className="section-header">
                        <h2>快速入门</h2>
                    </div>
                    <div className="quick-start-cards">
                        <div className="quick-start-card">
                            <div className="card-icon">
                                <svg viewBox="0 0 16 16" width="24" height="24">
                                    <path
                                        fill="currentColor"
                                        d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z"
                                    />
                                </svg>
                            </div>
                            <h3>创建仓库</h3>
                            <p>开始托管您的代码项目</p>
                        </div>
                        <div className="quick-start-card">
                            <div className="card-icon">
                                <svg viewBox="0 0 16 16" width="24" height="24">
                                    <path
                                        fill="currentColor"
                                        d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Z"
                                    />
                                </svg>
                            </div>
                            <h3>分支管理</h3>
                            <p>使用分支进行协作开发</p>
                        </div>
                        <div className="quick-start-card">
                            <div className="card-icon">
                                <svg viewBox="0 0 24 24" width="24" height="24">
                                    <path
                                        fill="currentColor"
                                        d="M12 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM10 14c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4Z"
                                    />
                                </svg>
                            </div>
                            <h3>邀请成员</h3>
                            <p>与团队一起协作开发</p>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default DashboardPage;
